/**
 * Schema.org JSON-LD builders for MiDiGi.NET.
 *
 * Pure functions that return plain objects; callers stringify into
 * `<script type="application/ld+json">`. No reactivity, no Vue lifecycle —
 * this is why the module lives under `utils/` (not `composables/`).
 *
 * Business profile fields (phone, email, address) are passed in by the caller
 * from `useRuntimeConfig().public` so they can be tuned per environment.
 */

import { COMMERCE_POLICY, effectiveSingleItemShippingFee } from './commercePolicy.ts'

export const SITE_URL = 'https://www.midigi.net'
export const ORG_NAME = 'MiDiGi'
export const ORG_DISPLAY_NAME = 'MiDiGi.NET'
export const ORG_LOGO = `${SITE_URL}/logo.svg`
export const ORG_FAVICON = `${SITE_URL}/favicon.svg`

/** Prevent authored content from closing the JSON-LD script element. */
export function serializeJsonLd(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029')
}

const ORG_ID = `${SITE_URL}/#organization`
const WEBSITE_ID = `${SITE_URL}/#website`
const LOCALBUSINESS_ID = `${SITE_URL}/#localbusiness`

export interface BusinessProfile {
  legalName?: string
  taxID?: string
  vatID?: string
  streetAddress?: string
  phone?: string
  email?: string
  addressLocality?: string
  addressRegion?: string
  addressCountry?: string
  priceRange?: string
  sameAs?: string[]
}

export interface SchemaBreadcrumbItem {
  name: string
  url: string
}

export interface SchemaFAQItem {
  question: string
  answer: string
}

export interface ProductSchemaInput {
  name: string
  description: string
  images: string[]
  /** Stable, human-readable identifier (typically the product slug). */
  sku: string
  /** Manufacturer part/model number (e.g. Apple "A2893") → schema.org mpn. */
  mpn?: string
  brandName?: string
  categoryName?: string
  priceEUR: number | null
  /** Effective product shipping fee; null means the product ships free. */
  shippingFeeEUR?: number | null
  /** Order subtotal at which this product's shipping template becomes free. */
  shippingFreeThresholdEUR?: number | null
  /** When > price, treated as a promotion; otherwise omitted. */
  originalPriceEUR?: number | null
  inStock: boolean
  /** Known product condition. Omit when the catalog has no authoritative value. */
  itemCondition?: 'https://schema.org/NewCondition' | 'https://schema.org/RefurbishedCondition'
  canonical: string
  /** Average of approved reviews (1 decimal). Omit when there are no reviews. */
  ratingValue?: number | null
  /** Count of approved reviews. aggregateRating is emitted only when > 0. */
  ratingCount?: number | null
  /** A few most-recent approved reviews → schema.org `review`. */
  reviews?: ProductReviewInput[]
}

export interface ProductReviewInput {
  author: string
  rating: number
  title?: string
  body?: string
  /** ISO date (YYYY-MM-DD or full ISO timestamp). */
  datePublished: string
}

function stripEmpty<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null) continue
    if (typeof v === 'string' && v.length === 0) continue
    if (Array.isArray(v) && v.length === 0) continue
    out[k] = v
  }
  return out as T
}

function isoDateInDays(days: number): string {
  const d = new Date()
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

function absoluteUrl(maybeUrl: string): string {
  if (!maybeUrl) return SITE_URL
  if (/^https?:\/\//i.test(maybeUrl)) return maybeUrl
  return `${SITE_URL}${maybeUrl.startsWith('/') ? '' : '/'}${maybeUrl}`
}

function contactPoint(profile?: BusinessProfile) {
  if (!profile?.phone && !profile?.email) return undefined
  return stripEmpty({
    '@type': 'ContactPoint',
    contactType: 'customer service',
    telephone: profile.phone,
    email: profile.email,
    areaServed: ['ES', 'PT', 'EU'],
    availableLanguage: ['es', 'en', 'pt', 'zh', 'fr'],
  })
}

export function buildOrganizationSchema(locale: string, profile?: BusinessProfile) {
  const cp = contactPoint(profile)
  return stripEmpty({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ORG_ID,
    name: ORG_NAME,
    legalName: profile?.legalName,
    taxID: profile?.taxID,
    vatID: profile?.vatID,
    url: SITE_URL,
    logo: ORG_LOGO,
    image: ORG_LOGO,
    inLanguage: locale,
    sameAs: profile?.sameAs,
    contactPoint: cp ? [cp] : undefined,
  })
}

export function buildWebSiteSchema(locale: string) {
  return stripEmpty({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: SITE_URL,
    name: ORG_DISPLAY_NAME,
    inLanguage: locale,
    publisher: { '@id': ORG_ID },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/shop?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  })
}

export function buildLocalBusinessSchema(locale: string, profile?: BusinessProfile) {
  const address = (profile?.streetAddress || profile?.addressLocality || profile?.addressRegion || profile?.addressCountry)
    ? stripEmpty({
        '@type': 'PostalAddress',
        streetAddress: profile?.streetAddress,
        addressLocality: profile?.addressLocality,
        addressRegion: profile?.addressRegion,
        addressCountry: profile?.addressCountry,
      })
    : undefined

  return stripEmpty({
    '@context': 'https://schema.org',
    '@type': 'ElectronicsStore',
    '@id': LOCALBUSINESS_ID,
    name: ORG_DISPLAY_NAME,
    legalName: profile?.legalName,
    taxID: profile?.taxID,
    vatID: profile?.vatID,
    image: ORG_LOGO,
    logo: ORG_LOGO,
    url: SITE_URL,
    telephone: profile?.phone,
    email: profile?.email,
    priceRange: profile?.priceRange,
    currenciesAccepted: 'EUR',
    paymentAccepted: 'Credit Card, Debit Card, Bank Transfer',
    inLanguage: locale,
    address,
    areaServed: ['ES', 'PT', 'EU'],
  })
}

export function buildBreadcrumbSchema(items: SchemaBreadcrumbItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: item.name,
      item: absoluteUrl(item.url),
    })),
  }
}

export function buildFAQSchema(faqs: SchemaFAQItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  }
}

function buildShippingDetails(p: ProductSchemaInput) {
  const fee = effectiveSingleItemShippingFee(
    p.shippingFeeEUR,
    p.shippingFreeThresholdEUR,
    p.priceEUR,
  )
  return {
    '@type': 'OfferShippingDetails',
    shippingRate: { '@type': 'MonetaryAmount', value: String(fee), currency: 'EUR' },
    shippingDestination: COMMERCE_POLICY.applicableCountries.map(c => ({
      '@type': 'DefinedRegion',
      addressCountry: c,
    })),
  }
}

function buildReturnPolicy() {
  return {
    '@type': 'MerchantReturnPolicy',
    applicableCountry: COMMERCE_POLICY.applicableCountries,
    returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
    merchantReturnDays: COMMERCE_POLICY.withdrawalDays,
    returnMethod: COMMERCE_POLICY.returnMethod,
    returnFees: COMMERCE_POLICY.ordinaryReturnFees,
  }
}

export function buildProductSchema(p: ProductSchemaInput) {
  const images = p.images.filter(Boolean).map(absoluteUrl)
  const isPromo = p.originalPriceEUR != null
    && p.priceEUR != null
    && p.originalPriceEUR > p.priceEUR

  const offers = stripEmpty({
    '@type': 'Offer',
    url: p.canonical,
    priceCurrency: 'EUR',
    price: p.priceEUR != null ? String(p.priceEUR) : undefined,
    // Only emit priceValidUntil when this is a real promo with a higher original price.
    // For stable prices Google's Offer spec allows omitting it.
    priceValidUntil: isPromo ? isoDateInDays(30) : undefined,
    availability: p.inStock
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock',
    itemCondition: p.itemCondition,
    seller: { '@id': ORG_ID },
    shippingDetails: buildShippingDetails(p),
    hasMerchantReturnPolicy: buildReturnPolicy(),
  })

  // Only emit aggregateRating when there is at least one approved review —
  // Google's review-snippet policy forbids empty/fabricated ratings.
  const hasRating = p.ratingCount != null && p.ratingCount > 0 && p.ratingValue != null
  const aggregateRating = hasRating
    ? {
        '@type': 'AggregateRating',
        ratingValue: p.ratingValue,
        reviewCount: p.ratingCount,
        bestRating: 5,
        worstRating: 1,
      }
    : undefined

  const review = (p.reviews && p.reviews.length > 0)
    ? p.reviews.map(r => stripEmpty({
        '@type': 'Review',
        author: { '@type': 'Person', name: r.author },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: r.rating,
          bestRating: 5,
          worstRating: 1,
        },
        name: r.title,
        reviewBody: r.body,
        datePublished: r.datePublished,
      }))
    : undefined

  return stripEmpty({
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': `${p.canonical}#product`,
    name: p.name,
    description: p.description,
    image: images.length > 0 ? images : undefined,
    sku: p.sku,
    mpn: p.mpn,
    url: p.canonical,
    brand: p.brandName
      ? { '@type': 'Brand', name: p.brandName }
      : undefined,
    category: p.categoryName,
    itemCondition: p.itemCondition,
    offers,
    aggregateRating,
    review,
  })
}
